import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Button,
  Flex,
  FormHeading,
  Grid,
  Label,
  Text,
  ErrorMsg,
  Toastify,
} from "atoms";
import { FormInput } from "molecules";
import { FormCheckbox, FormRadio } from "molecules/FormInput";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { FileUpload } from "atoms/FileUpload";
import { useParams } from "react-router-dom";
import { usePropsoch } from "contexts/Propsoch";
import { optionFetch } from "utils/utilities";

export const NeighbourhoodForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingNeighbourhood,
      createPropertyListingNeighbourhood,
      updatePropertyListingNeighbourhood,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingNeighbourhood(id)?.then((data) => {
        setProperty(data);

        const choosenAreaImages = {};
        data?.nearbyAreas?.forEach((item) => {
          choosenAreaImages[item?.nearbyAreaId?.toString()] = {};
          choosenAreaImages[item?.nearbyAreaId?.toString()].images =
            item?.images;
        });
        // reformat type
        data &&
          setProperty((prev) => ({
            ...prev,
            choosenArea: data?.nearbyAreas?.map((item) => item?.nearbyAreaId),
            choosenAreaImages: choosenAreaImages,
          }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({
      longitude,
      latitude,
      propertyAreaType,
      choosenArea,
      choosenAreaImages,
      dirty,
      nearbyAreaComments,
      neighbourhoodImage,
      neighbourhoodImageAlt,
    }) => {
      setSubmitLoading(true);
      // new nearbyAreas total
      const nearbyAreas = choosenArea?.map((item) => ({
        nearbyAreaId: item,
        images: choosenAreaImages?.[item]?.images || [],
      }));

      const formattedValues = {
        propertyId: id,
        longitude,
        latitude,
        propertyAreaType,
        nearbyAreas,
        nearbyAreaComments,
        neighbourhoodImage,
        neighbourhoodImageAlt,
      };

      if (dirty) {
        if (property?.latitude) {
          // new nearbyAreas deleted
          const deletedImages = [];

          // new nearbyAreas added
          const newImages = [];

          nearbyAreas?.forEach((area) => {
            const prevAreaImages =
              property?.nearbyAreas?.find(
                (item) => item?.nearbyAreaId === area?.nearbyAreaId
              )?.images || [];

            const areaDeletedImages = [];
            const areaNewImages = [];
            prevAreaImages?.forEach((image) => {
              const inclusionIndex = area?.images?.indexOf(image);
              if (inclusionIndex <= -1) {
                areaDeletedImages.push(image);
              }
            });
            area?.images?.forEach((image) => {
              const inclusionIndex = prevAreaImages?.indexOf(image);
              if (inclusionIndex <= -1) {
                areaNewImages.push(image);
              }
            });

            if (areaNewImages?.length) {
              newImages.push({
                nearbyAreaId: area?.nearbyAreaId,
                images: areaNewImages,
              });
            }
            if (areaDeletedImages?.length) {
              deletedImages.push({
                nearbyAreaId: area?.nearbyAreaId,
                images: areaDeletedImages,
              });
            }
          });

          updatePropertyListingNeighbourhood(
            { ...formattedValues, newImages, deletedImages },
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Neighbourhood updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Neighbourhood not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingNeighbourhood(
            formattedValues,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Neighbourhood created");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Neighbourhood not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingNeighbourhood,
      id,
      updatePropertyListingNeighbourhood,
      onNext,
      property,
      rootProperty,
      fetchProperty,
    ]
  );

  // nearby area
  const [nearbyArea, setNearbyArea] = useState([]);
  useEffect(() => {
    // nearby area
    optionFetch("/nearbyareas/get", "name", "id").then((data) => {
      setNearbyArea(data);
    });
  }, []);

  // validation schema
  const validationSchema = Yup.object().shape({
    latitude: Yup.string()
      .typeError("Latitude is required")
      .required("Latitude is required")
      .matches(
        /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,6})?))$/,
        "Invalid Latitude"
      ),
    longitude: Yup.string()
      .typeError("Longitude is required")
      .required("Longitude is required")
      .matches(
        /^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/,
        "Invalid Longitude"
      ),
    propertyAreaType: Yup.string()
      .typeError("Choose Area Type")
      .required("Choose Area Type"),
    nearbyAreaComments: Yup.string()
      .nullable()
      .when("choosenArea", {
        is: (v) =>
          [...v]?.filter((i) =>
            nearbyArea
              ?.find((t) => t?.value === i)
              ?.label?.toLowerCase()
              ?.includes("other")
          )?.length > 0,
        then: Yup.string().required("Add comments"),
      }),
    neighbourhoodImage: Yup.string()
      .typeError("Choose an image")
      .required("Choose an image"),
  });
  return (
    <Formik
      initialValues={{
        latitude: "",
        longitude: "",
        propertyAreaType: "",
        choosenArea: "",
        choosenAreaImages: {},
        nearbyAreaComments: "",
        neighbourhoodImage: "",
        neighbourhoodImageAlt: "",
        ...property,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, submitForm, dirty, touched }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });
        return (
          <Form>
            <Grid gridGap="2.4rem">
              <FormHeading text="Neighbourhood" />
              <Label text="Enter Location Coordinates of the Property" req />
              <Grid
                gridTemplateColumns="1fr 1fr"
                gridColumnGap="2.4rem"
                gridRowGap="1.6rem"
              >
                <FormInput
                  name="latitude"
                  type="text"
                  placeholder="Enter Latitude"
                  req
                />
                <FormInput
                  name="longitude"
                  type="text"
                  placeholder="Enter Longitude"
                  req
                />
              </Grid>
              <Box>
                <Label text="Area Type" mb="1.6rem" req />
                <Grid
                  gridAutoFlow="column"
                  gridGap="2.4rem"
                  justifyContent="start"
                  alignItems="center"
                  mb="2.4rem"
                >
                  <FormRadio
                    name="propertyAreaType"
                    label="Residential"
                    value="residential"
                  />
                  <FormRadio
                    name="propertyAreaType"
                    label="Commercial"
                    value="commercial"
                  />
                  <FormRadio
                    name="propertyAreaType"
                    label="Industrial"
                    value="industrial"
                  />
                </Grid>
                {touched?.propertyAreaType && errors?.propertyAreaType && (
                  <ErrorMsg text={errors?.propertyAreaType} />
                )}
              </Box>

              <Label text="Nearby areas" />
              <Grid
                // gridAutoFlow="column"
                gridTemplateColumns="repeat(5,1fr)"
                gridGap="3rem"
                justifyContent="start"
                mb="1rem"
              >
                {nearbyArea?.map((item, index) => (
                  <Flex aI="center" key={index}>
                    <FormCheckbox
                      name="choosenArea"
                      height="1.8rem"
                      value={item?.value}
                    />
                    <Text color="neutral.700">{item?.label}</Text>
                  </Flex>
                ))}
              </Grid>

              {[...values?.choosenArea]?.filter((i) =>
                nearbyArea
                  ?.find((t) => t?.value === i)
                  ?.label?.toLowerCase()
                  ?.includes("other")
              )?.length > 0 && (
                <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                  <Grid gridRowGap="1.6rem">
                    <Label text="Comments" req />
                    <FormInput
                      name="nearbyAreaComments"
                      type="text"
                      placeholder="Enter Comments"
                      req
                    />
                  </Grid>
                </Grid>
              )}
              {/* this will map according to checkbox selected */}
              {Array.isArray(values?.choosenArea) &&
                values?.choosenArea
                  ?.filter(
                    (i) =>
                      !nearbyArea
                        ?.find((t) => t?.value === i)
                        ?.label?.toLowerCase()
                        ?.includes("other")
                  )
                  ?.map((currentArea, index) => (
                    <Grid key={index}>
                      <Label
                        text={`Upload ${
                          nearbyArea?.find((i) => i?.value === currentArea)
                            ?.label
                        } Pictures`}
                        mb="1.6rem"
                      />
                      <FileUpload
                        files={
                          values?.choosenAreaImages?.[currentArea.toString()]
                            ?.images
                        }
                        onUpload={(locations) => {
                          const newProjectImages = [
                            ...(values?.choosenAreaImages?.[
                              currentArea.toString()
                            ]?.images
                              ? JSON.parse(
                                  JSON.stringify(
                                    values?.choosenAreaImages?.[
                                      currentArea.toString()
                                    ]?.images
                                  )
                                ) || []
                              : []),
                            ...locations?.map((i) => ({ image: i, alt: "" })),
                            // ...locations?.map((i) => ({
                            //   image: i,
                            //   description: "",
                            // })),
                          ];

                          setFieldValue(
                            `choosenAreaImages.${currentArea.toString()}.images`,
                            newProjectImages
                          );
                        }}
                        onSubmitDescription={({ alt, file }) => {
                          setFieldValue(
                            `choosenAreaImages.${currentArea.toString()}.images.[${
                              file?.index
                            }].alt`,
                            alt
                          );
                        }}
                        // onSubmitDescription={({ description, file }) => {
                        //   setFieldValue(
                        //     `choosenAreaImages.${currentArea.toString()}.images.[${
                        //       file?.index
                        //     }].description`,
                        //     description
                        //   );
                        // }}
                        onCross={(index) => {
                          const currProjectImages = [
                            ...(values?.choosenAreaImages?.[
                              currentArea.toString()
                            ]?.images
                              ? JSON.parse(
                                  JSON.stringify(
                                    values?.choosenAreaImages?.[
                                      currentArea.toString()
                                    ]?.images
                                  )
                                ) || []
                              : []),
                          ];
                          const total = currProjectImages?.length;

                          const newProjectImages = [
                            ...currProjectImages?.slice(0, index),
                            ...currProjectImages?.slice(index + 1, total),
                          ];

                          setFieldValue(
                            `choosenAreaImages.${currentArea.toString()}.images`,
                            newProjectImages
                          );
                        }}
                      />
                    </Grid>
                  ))}
              <Box>
                <Label text="Upload Neighbourhood Image" mb="1.6rem" req />
                <FileUpload
                  files={
                    values?.neighbourhoodImage
                      ? [
                          {
                            image: values?.neighbourhoodImage,
                            alt: values?.neighbourhoodImageAlt,
                          },
                        ]
                      : []
                  }
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.images
                        ? JSON.parse(JSON.stringify(values?.images)) || []
                        : []),
                      ...locations,
                    ];

                    setFieldValue("neighbourhoodImage", newProjectImages[0]);
                  }}
                  isMultiple={false}
                  onSubmitDescription={({ alt }) => {
                    setFieldValue(`neighbourhoodImageAlt`, alt);
                  }}
                  onCross={(index) => {
                    setFieldValue("neighbourhoodImage", "");
                    setFieldValue("neighbourhoodImageAlt", "");
                  }}
                />
                {touched?.neighbourhoodImage && errors?.neighbourhoodImage && (
                  <ErrorMsg text={errors?.neighbourhoodImage} />
                )}
              </Box>
              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
